import { useRef, useState } from 'react';
import type { TagDto } from '@pb/shared';
import { errorMessage } from '../api/client';
import { createTag, deleteTag, updateTag } from '../api/tags';
import { useProject } from '../lib/project';
import { TypedConfirmDialog } from './ConfirmDialogs';
import { Dialog } from './Dialog';
import { ErrorAlert } from './States';
import { pluralize } from '../lib/text';

function TagRow({ tag, onDelete }: { tag: TagDto; onDelete: () => void }) {
  const { reloadTags } = useProject();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(tag.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const save = async () => {
    if (!name.trim() || name.trim() === tag.name) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await updateTag(tag.id, name.trim());
      await reloadTags();
      setEditing(false);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="stack" style={{ gap: 4 }}>
      <div className="row">
        {editing ? (
          <form
            className="row grow"
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
          >
            <input
              className="input"
              value={name}
              maxLength={40}
              aria-label={`New name for tag ${tag.name}`}
              onChange={(event) => setName(event.target.value)}
              autoFocus
            />
            <button type="submit" className="btn btn-sm btn-primary" disabled={busy || !name.trim()}>
              Save
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => {
                setEditing(false);
                setName(tag.name);
                setError(null);
              }}
            >
              Cancel
            </button>
          </form>
        ) : (
          <>
            <span className="tag">{tag.name}</span>
            <span className="small faint grow">used {pluralize(tag.usage_count, 'time')}</span>
            <button type="button" className="btn btn-sm" onClick={() => setEditing(true)}>
              Rename
            </button>
            <button type="button" className="btn btn-sm" onClick={onDelete}>
              Delete
            </button>
          </>
        )}
      </div>
      <ErrorAlert error={error} />
    </li>
  );
}

export function TagManagerDialog({ onClose }: { onClose: () => void }) {
  const { project, tags, reloadTags } = useProject();
  const [name, setName] = useState('');
  const [error, setError] = useState<{ name: string; err: unknown } | null>(null);
  const [deleting, setDeleting] = useState<TagDto | null>(null);
  const queue = useRef<string[]>([]);
  const draining = useRef(false);

  const drain = async () => {
    if (draining.current) return;
    draining.current = true;
    try {
      for (let value = queue.current.shift(); value !== undefined; value = queue.current.shift()) {
        try {
          await createTag(project.id, value);
          setError(null);
        } catch (err) {
          const failed = value;
          setError({ name: failed, err });
          setName((current) => (current === '' ? failed : current));
        }
      }
      await reloadTags();
    } finally {
      draining.current = false;
      if (queue.current.length > 0) void drain();
    }
  };

  const submit = () => {
    const value = name.trim();
    if (!value) return;
    setName('');
    queue.current.push(value);
    void drain();
  };

  return (
    <>
      <Dialog
        title="Project tags"
        onClose={onClose}
        footer={
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        }
      >
        <p className="small muted">
          Tags can be set on slides, groups and presentations of this project. Names are 1–40 characters and unique.
        </p>
        <form
          className="row"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <input
            className="input"
            placeholder="New tag name"
            aria-label="New tag name"
            value={name}
            maxLength={40}
            onChange={(event) => setName(event.target.value)}
          />
          <button type="submit" className="btn btn-primary" disabled={!name.trim()}>
            Add tag
          </button>
        </form>
        {error ? (
          <div className="alert alert-error" role="alert">
            Could not create “{error.name}”: {errorMessage(error.err)}
          </div>
        ) : null}
        {tags.length === 0 ? (
          <p className="muted">No tags yet. Add the first one above.</p>
        ) : (
          <ul className="list-plain">
            {tags.map((tag) => (
              <TagRow key={tag.id} tag={tag} onDelete={() => setDeleting(tag)} />
            ))}
          </ul>
        )}
      </Dialog>
      {deleting ? (
        <TypedConfirmDialog
          title="Delete tag"
          name={deleting.name}
          what="tag name"
          actionLabel="Delete tag"
          onClose={() => setDeleting(null)}
          onConfirm={async (confirm) => {
            await deleteTag(deleting.id, confirm);
            await reloadTags();
            setDeleting(null);
          }}
        >
          <p>
            Deleting <strong>{deleting.name}</strong> removes it from {pluralize(deleting.usage_count, 'item')}. This
            cannot be undone.
          </p>
        </TypedConfirmDialog>
      ) : null}
    </>
  );
}
